import { shallowEqual, useSelector } from 'react-redux';
import {
  useCallback, useEffect, useRef
} from 'react';
import useLiveAudioTracklist from './useLiveAudioTracklist';
import {
  selectAudioPaused,
  selectCurrentEpisode
} from '@/state/selectors/audio';
import useGA from '@/hooks/ga/useGA';
import { GAstreamedTrack } from '@/helpers/events-ga/audio';

const selector = (state) => ({
  isPaused: selectAudioPaused(state),
  currentEpisode: selectCurrentEpisode(state)
});

const useAudioTrackStreamEvents = () => {
  const { isPaused, currentEpisode } = useSelector(selector, shallowEqual);
  const currentTrackStreamSentRef = useRef(false);
  const { sendGAEvent } = useGA();
  const {
    currentTrackArtist: trackArtist,
    currentTrackTitle: trackTitle
  } = useLiveAudioTracklist();

  const tracklistId = `${ trackArtist }${ trackTitle }`;

  const handleSendTrackStreamEvent = useCallback(() => {
    const {
      id: episodeId,
      episode_number: episodeNumber,
      season: seasonNumber,
      show: episodeShow
    } = currentEpisode || {};

    if (episodeId && trackArtist && trackTitle) {
      currentTrackStreamSentRef.current = true;
      
      const { id: showId, title: showTitle } = episodeShow;

      const trackStreamEventParams = {
        episodeId,
        episodeNumber,
        seasonNumber,
        showId,
        showTitle,
        trackTitle,
        trackArtist
      };
      
      // GA stream start event
      sendGAEvent({ ...GAstreamedTrack(trackStreamEventParams) });
    }
  }, [currentTrackStreamSentRef, sendGAEvent, currentEpisode, trackArtist, trackTitle]);

  useEffect(() => {
    // The track has changed, so make sure to mark the track stream event as unsent
    currentTrackStreamSentRef.current = false;
  }, [tracklistId]);

  useEffect(() => {
    if (!isPaused && !currentTrackStreamSentRef.current && !!tracklistId) {
      currentTrackStreamSentRef.current = true;

      // Send track stream event
      handleSendTrackStreamEvent();
    }
  }, [handleSendTrackStreamEvent, isPaused, tracklistId]);
};

export default useAudioTrackStreamEvents;